import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  exchangeGoogleIdToken,
  fetchAgentAssetEvolutionCandidates,
  fetchDashboardCostGovernance,
  fetchAgentRoleAssetDetail,
  fetchAgentRoleAssets,
  fetchDashboardOverview,
  fetchDashboardRolloutHealth,
  fetchDashboardTopList,
  fetchEmployeeSessions,
  fetchEmployeeSummary,
  fetchSkillProposalDetail,
  fetchSkillProposals,
  fetchSessionEvents,
  publishAgentRoleAsset,
  retireAgentRoleAsset,
  reviewSkillProposal,
  rollbackAgentRoleAsset,
} from './index';
import { CLAUDE_CODE_DASHBOARD_QUERY_KEYS } from './queryKeys';
import { AGENT_DASHBOARD_TOKEN_STORAGE_KEY } from './instance';
import type {
  AgentRoleAssetListFilters,
  AgentRoleAssetRollbackRequest,
  AgentRoleAssetTransitionRequest,
  DashboardRolloutHealthFilters,
  DashboardSessionResponse,
  DashboardTopListKind,
  SkillProposalReviewRequest,
} from './types';

export const useExchangeGoogleIdToken = (onSuccess?: (session: DashboardSessionResponse) => void) =>
  useMutation({
    mutationFn: (idToken: string) => exchangeGoogleIdToken(idToken),
    onSuccess: (session) => {
      localStorage.setItem(AGENT_DASHBOARD_TOKEN_STORAGE_KEY, session.token);
      onSuccess?.(session);
    },
  });

interface DashboardQueryOptions {
  enabled?: boolean;
}

export const useDashboardOverview = (windowDays: number, options?: DashboardQueryOptions) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.overview(windowDays),
    queryFn: () => fetchDashboardOverview(windowDays),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });

export const useDashboardCostGovernance = (windowDays: number, limit: number, options?: DashboardQueryOptions) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.costGovernance(windowDays, limit),
    queryFn: () => fetchDashboardCostGovernance(windowDays, limit),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });

export const useDashboardRolloutHealth = (
  windowDays: number,
  filters: DashboardRolloutHealthFilters,
  options?: DashboardQueryOptions,
) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.rolloutHealth(windowDays, filters),
    queryFn: () => fetchDashboardRolloutHealth(windowDays, filters),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });

export const useDashboardTopList = (
  kind: DashboardTopListKind,
  windowDays: number,
  limit: number,
  offset: number,
  options?: DashboardQueryOptions,
) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.topList(kind, windowDays, limit, offset),
    queryFn: () => fetchDashboardTopList(kind, windowDays, limit, offset),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });

export const useAgentAssetEvolutionCandidates = (windowDays: number, limit: number, options?: DashboardQueryOptions) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.assetEvolutionCandidates(windowDays, limit),
    queryFn: () => fetchAgentAssetEvolutionCandidates(windowDays, limit),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });

export const useEmployeeSummary = (employeeId: string, windowDays: number, options?: DashboardQueryOptions) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.employeeSummary(employeeId, windowDays),
    queryFn: () => fetchEmployeeSummary(employeeId, windowDays),
    enabled: (options?.enabled ?? true) && employeeId.length > 0,
    staleTime: 60_000,
  });

export const useEmployeeSessions = (
  employeeId: string,
  windowDays: number,
  limit: number,
  offset: number,
  options?: DashboardQueryOptions,
) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.employeeSessions(employeeId, windowDays, limit, offset),
    queryFn: () => fetchEmployeeSessions(employeeId, windowDays, limit, offset),
    enabled: (options?.enabled ?? true) && employeeId.length > 0,
    staleTime: 60_000,
  });

export const useSessionEvents = (sessionId: string, limit: number, options?: DashboardQueryOptions) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.sessionEvents(sessionId, limit),
    queryFn: () => fetchSessionEvents(sessionId, limit),
    enabled: (options?.enabled ?? true) && sessionId.length > 0,
    staleTime: 60_000,
  });

export const useSkillProposals = (
  params: { status?: string; proposalSource?: string; employeeId?: string; role?: string; limit?: number },
  options?: DashboardQueryOptions,
) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.skillProposals(
      params.status ?? '',
      params.proposalSource ?? '',
      params.employeeId ?? '',
      params.role ?? '',
      params.limit ?? 100,
    ),
    queryFn: () => fetchSkillProposals(params),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });

export const useSkillProposalDetail = (proposalId: string, options?: DashboardQueryOptions) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.skillProposalDetail(proposalId),
    queryFn: () => fetchSkillProposalDetail(proposalId),
    enabled: (options?.enabled ?? true) && proposalId.length > 0,
    staleTime: 30_000,
  });

export const useReviewSkillProposal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: SkillProposalReviewRequest) => reviewSkillProposal(params),
    onSuccess: (_proposal, params) => {
      void queryClient.invalidateQueries({
        queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.skillProposalDetail(params.proposalId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['claude-code-dashboard', 'skill-proposals'],
      });
    },
  });
};

export const useAgentRoleAssets = (filters: AgentRoleAssetListFilters, options?: DashboardQueryOptions) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.roleAssets(filters),
    queryFn: () => fetchAgentRoleAssets(filters),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });

export const useAgentRoleAssetDetail = (assetId: string, options?: DashboardQueryOptions) =>
  useQuery({
    queryKey: CLAUDE_CODE_DASHBOARD_QUERY_KEYS.roleAssetDetail(assetId),
    queryFn: () => fetchAgentRoleAssetDetail(assetId),
    enabled: (options?.enabled ?? true) && assetId.length > 0,
    staleTime: 30_000,
  });

export const usePublishAgentRoleAsset = () =>
  useMutation({
    mutationFn: (params: AgentRoleAssetTransitionRequest) => publishAgentRoleAsset(params),
  });

export const useRetireAgentRoleAsset = () =>
  useMutation({
    mutationFn: (params: AgentRoleAssetTransitionRequest) => retireAgentRoleAsset(params),
  });

export const useRollbackAgentRoleAsset = () =>
  useMutation({
    mutationFn: (params: AgentRoleAssetRollbackRequest) => rollbackAgentRoleAsset(params),
  });
